import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper } from '@mui/material';
import { 
  Add as AddIcon, 
  AutoAwesome as AutoAwesomeIcon 
} from '@mui/icons-material';

/**
 * Empty state view for the Build component when no tasks are available
 * @param {Object} props - Component props
 * @param {Function} props.onAddTasks - Function to add tasks manually
 * @param {Function} props.onGeneratePlan - Function to generate a build plan automatically
 * @returns {React.ReactElement} Empty state view component
 */
const EmptyStateView = ({ onAddTasks, onGeneratePlan }) => {
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        width: '100%',
        textAlign: 'center',
        px: 3
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 5,
          borderRadius: 2,
          backgroundColor: '#f7f9fc',
          maxWidth: '700px',
          width: '100%',
          border: '1px dashed #ccd7e0'
        }}
      >
        <Typography 
          variant="h5" 
          component="h2"
          sx={{ 
            fontWeight: 600,
            mb: 2,
            color: theme => theme.palette.text.primary
          }}
        >
          No Development Plan Available
        </Typography>
        
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ mb: 4, maxWidth: '550px', mx: 'auto' }}
        >
          You don't have a development plan for this project yet. You can generate a plan automatically based on your project's user stories, features, and technology choices, or add tasks manually.
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            gap: 2
          }}
        >
          {onGeneratePlan && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<AutoAwesomeIcon />}
              onClick={onGeneratePlan}
            >
              Generate Plan Automatically
            </Button>
          )}
          
          {onAddTasks && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<AddIcon />}
              onClick={onAddTasks}
            >
              Add Tasks Manually
            </Button>
          )}
        </Box>
        
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block', 
            mt: 3,
            fontStyle: 'italic'
          }}
        >
          Tip: Automatic plan generation works best if you've already created user stories, features, and selected technologies.
        </Typography>
      </Paper>
    </Box>
  );
};

EmptyStateView.propTypes = {
  onAddTasks: PropTypes.func,
  onGeneratePlan: PropTypes.func
};

export default EmptyStateView;